<template>
  <div class="p-6 space-y-2">
    <!-- <h2 class="text-2xl font-semibold text-gray-800">📞 Call Flow Analyser</h2> -->

    <div v-if="calls && calls.length" class="space-y-2">
      <div
        v-for="(call, index) in calls"
        :key="call.call_id"
        class="bg-white shadow rounded-2xl border border-gray-200 overflow-hidden"
      >
        <!-- Header -->
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition"
          @click="toggleOpen(index)"
        >
          <!-- Left side: call info -->
          <div class="flex gap-4 items-center">
            <div class="">
              <h3
                class="text-lg font-semibold text-gray-800 flex items-center gap-2"
              >
                <!-- <span
                class="inline-flex items-center justify-center w-2.5 h-2.5 rounded-full"
                :class="{
                  'bg-green-500': call.direction === 'inbound',
                  'bg-blue-500': call.direction === 'internal',
                  'bg-yellow-500': call.direction === 'outbound',
                }"
              ></span> -->
                <span
                  class="h-9 w-9 rounded-full flex items-center justify-center ring-2"
                  :class="{
                    'bg-green-500 ring-green-600': call.direction === 'inbound',
                    'bg-yellow-500 ring-yellow-600':
                      call.direction === 'outbound',
                    'bg-blue-500 ring-blue-600':
                      call.direction !== 'inbound' &&
                      call.direction !== 'outbound',
                  }"
                >
                  <svg
                    v-if="call.direction === 'inbound'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="1 -1 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-phone-incoming-icon lucide-phone-incoming text-white"
                  >
                    <path d="M16 2v6h6" />
                    <path d="m22 2-6 6" />
                    <path
                      d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"
                    />
                  </svg>
                  <svg
                    v-else-if="call.direction === 'outbound'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="1 -1 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-phone-outgoing-icon lucide-phone-outgoing text-white"
                  >
                    <path d="m16 8 6-6" />
                    <path d="M22 8V2h-6" />
                    <path
                      d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-phone-icon lucide-phone text-white"
                  >
                    <path
                      d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"
                    />
                  </svg>
                </span>
                <!-- {{
                $t(
                  call.direction.charAt(0).toUpperCase() +
                    call.direction.slice(1)
                )
              }} -->
                <!-- <span class="font-semibold text-xs text-gray-400"
                >#{{ call.call_id }}</span
              > -->
              </h3>
            </div>
            <div class="">
              <p class="text-sm text-gray-700">
                {{ $t("from") }}
                <span class="font-bold">{{
                  call.direction === "internal" ||
                  call.direction === "outbound" ||
                  call.direction === "test" ||
                  call.direction === "conference"
                    ? pbxMap?.extensions[call?.caller]
                    : call.caller
                }}</span>
                <!-- <span v-if="call.did"> → {{ call.did }}</span> -->
              </p>
              <p class="text-sm text-gray-500">
                {{ formatDate(call.start) }} → {{ formatDate(call.end) }} ({{
                  formatDuration(call.start, call.end)
                }})
              </p>
            </div>
          </div>

          <!-- Right side: flow preview and actions -->
          <div class="mt-1 sm:mt-0 text-right space-y-2 sm:space-y-3">
            <!-- Flow preview -->
            <div
              class="flex flex-wrap justify-end items-center text-xs text-gray-600 max-w-2xl"
            >
              <template v-for="(step, i) in getFlowPreview(call.flow)" :key="i">
                <span class="flex items-center gap-1">
                  <span
                    :class="
                      getFlowIconClass(step.type, step.answered, step.pong)
                    "
                  >
                    <font-awesome-icon :icon="getFlowIcon(step.type)" />
                  </span>
                  <span class="hidden sm:inline">{{ step.name }}</span>
                </span>
                <span
                  v-if="i < getFlowPreview(call.flow).length - 1"
                  class="mx-1 text-gray-400"
                  >→</span
                >
              </template>
            </div>

            <!-- Toggle button -->
            <button
              class="text-xs px-3 py-1 bg-teal-100 hover:bg-teal-200 text-teal-800 rounded-full font-medium transition"
              @click.stop="toggleOpen(index)"
            >
              {{ openIndex === index ? $t("hideDetails") : $t("viewDetails") }}
            </button>
          </div>
        </div>

        <!-- Flow Timeline (collapsible) -->
        <transition name="fade">
          <div v-if="openIndex === index" class="p-6">
            <ol class="relative border-l border-gray-300">
              <li
                v-for="(step, idx) in call.flow"
                :key="idx"
                class="mb-6 ml-6 group"
              >
                <!-- Timeline marker -->
                <span
                  v-if="step.segment_type === 'ping'"
                  :class="[
                    step.answered ? 'bg-teal-500' : 'bg-red-500',
                    'absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ',
                  ]"
                ></span>

                <div
                  v-if="step.segment_type === 'ping'"
                  class="p-4 bg-gray-50 rounded-xl shadow-sm hover:bg-gray-100 transition"
                >
                  <div class="flex flex-wrap items-baseline justify-between">
                    <h4 class="text-sm font-semibold text-gray-800">
                      {{
                        step.from_type === "world" &&
                        step.to_type === "world" &&
                        call.caller
                          ? call.caller
                          : step.from_type === "world" && call.did
                          ? didName(call.did)
                          : step.from_name
                          ? step.from_name
                          : (step.from_type === "extension"
                              ? pbxMap?.extensions[step.from_dn]
                              : step.from_type === "queue"
                              ? pbxMap?.call_queues[step.from_dn]?.name
                              : step.from_dn) +
                            " (" +
                            step.from_dn +
                            ")"
                      }}
                      <span
                        v-if="step.from_type !== 'world'"
                        class="text-gray-400 text-xs"
                        >({{
                          step.from_type === "extension" ||
                          step.from_type === "call_flow" ||
                          step.from_type === "queue"
                            ? $t(step.from_type)
                            : step.from_type
                        }})</span
                      >
                      →
                      {{
                        step.to_name
                          ? step.to_name
                          : (step.to_type === "extension"
                              ? pbxMap?.extensions[step.to_dn]
                              : step.to_type === "queue"
                              ? pbxMap?.call_queues[step.to_dn]?.name
                              : step.to_dn) +
                            " (" +
                            step.to_dn +
                            ")"
                      }}
                      <span
                        v-if="step.to_type !== 'world'"
                        class="text-gray-400 text-xs"
                        >({{
                          step.to_type === "extension" ||
                          step.to_type === "call_flow" ||
                          step.to_type === "queue"
                            ? $t(step.to_type)
                            : step.to_type
                        }})</span
                      >
                    </h4>
                  </div>
                  <p class="mt-1 text-sm text-gray-600">
                    {{ formatDate(step.started_at) }} →
                    {{ formatDate(step.ended_at) }} · {{ $t("duration") }}:
                    <span class="font-medium">{{
                      formatDuration(step.started_at, step.ended_at)
                    }}</span>
                  </p>
                  <p
                    v-if="
                      step.action === 'terminé par la source' ||
                      step.action === 'terminé l\'appel'
                    "
                    class="mt-2 text-xs text-gray-500"
                  >
                    {{
                      step.action === "terminé par la source"
                        ? "terminé par " +
                          (step.from_type === "extension"
                            ? pbxMap?.extensions[step.from_dn]
                            : step.from_type === "queue"
                            ? pbxMap?.call_queues[step.from_dn]?.name
                            : step.from_dn)
                        : step.action === "terminé l'appel"
                        ? "terminé par " +
                          (step.to_type === "extension"
                            ? pbxMap?.extensions[step.to_dn]
                            : step.to_type === "queue"
                            ? pbxMap?.call_queues[step.to_dn]?.name
                            : step.to_dn)
                        : step.action
                    }}
                  </p>
                  <p v-if="step.to_name" class="mt-1 text-xs text-gray-400">
                    Target: {{ step.to_name }}
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </transition>
      </div>
      <!-- Pagination Controls -->
      <div
        v-if="pagination.total > 0"
        class="flex justify-between items-center mt-6"
      >
        <div class="text-sm text-gray-500">
          Showing
          <span class="font-semibold">{{ pagination.current_page }}</span> /
          {{ pagination.last_page }}
          — Total:
          <span class="font-semibold">{{ pagination.total }}</span> calls
        </div>

        <div class="flex items-center space-x-2">
          <button
            class="px-3 py-1 border rounded-lg text-sm hover:bg-gray-100"
            :disabled="pagination.current_page === 1"
            @click="onPageChange(pagination.current_page - 1)"
          >
            ‹ Prev
          </button>

          <button
            class="px-3 py-1 border rounded-lg text-sm hover:bg-gray-100"
            :disabled="pagination.current_page === pagination.last_page"
            @click="onPageChange(pagination.current_page + 1)"
          >
            Next ›
          </button>

          <!-- <button
            class="ml-3 px-3 py-1 border rounded-lg text-sm bg-teal-100 hover:bg-teal-200 text-teal-800"
            @click="onRefresh"
          >
            🔄 Refresh
          </button> -->
        </div>
      </div>
    </div>

    <!-- No data -->
    <div v-else class="text-center py-20 text-gray-400">
      <font-awesome-icon icon="search" class="text-5xl text-teal-500 mb-4" />
      <p class="text-lg">Aucun appel trouvé pour cette période</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex/dist/vuex.cjs.js";

export default {
  name: "CallFlowAnalyser",
  props: {
    calls: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      required: false,
      default: () => ({
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1,
      }),
    },
    onPageChange: {
      type: Function,
      required: false,
    },
    onRefresh: {
      type: Function,
      required: false,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      openIndex: null,
    };
  },
  methods: {
    didName(did) {
      let didName = did;
      if (this.pbxMap?.dids?.length) {
        didName = this.pbxMap?.dids.filter((r) => {
          let name = "";
          if (r.replace(/\*/g, "0").includes(did)) {
            name = r;
          }
          return name;
        });
      }
      return didName[0].replace(/\*/g, "0");
    },
    toggleOpen(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatDuration(start, end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diff = Math.floor((endDate - startDate) / 1000);
      const min = Math.floor(diff / 60);
      const sec = diff % 60;
      return `${min}m ${sec}s`;
    },
    /** Simplified visual flow (unique sequence) */
    getFlowPreview(flow) {
      const aggregatedFlow = [];

      flow.forEach((f) => {
        // 1. Skip non-ping segments
        if (f.segment_type !== "ping") {
          return;
        }

        // 2. Identify the current segment's key name
        const currentName = f.to_name
          ? f.to_name
          : f.to_dn
          ? f.to_type === "extension"
            ? this.pbxMap?.extensions[f.to_dn]
            : f.to_type === "queue"
            ? this.pbxMap?.call_queues[f.to_dn]?.name
            : f.to_dn
          : f.to_dn;

        // 3. Get the last item added to the aggregated array
        const lastAggregatedItem = aggregatedFlow[aggregatedFlow.length - 1];

        // 4. Check for consecutive repetition
        if (
          lastAggregatedItem &&
          lastAggregatedItem.originalName === currentName
        ) {
          // It's a repetition: Increment the count of the last item
          lastAggregatedItem.count = (lastAggregatedItem.count || 1) + 1;
        } else {
          // It's a new or different name: Push a new entry
          aggregatedFlow.push({
            type: f.to_type,
            answered: f.answered,
            segment_type: f.segment_type,
            // Store the original name and initial count internally
            originalName: currentName,
            count: 1,
          });
        }
      });

      // 5. Format the final output array (array of objects)
      const finalSimplified = aggregatedFlow.map((node) => {
        let displayName = node.originalName;

        if (node.count > 1 && node.segment_type === "ping") {
          // Add the count in parentheses if it's greater than 1
          displayName = `${node.originalName} (${node.count})`;
        }

        // Return the object in the required format: { type: ..., name: ... }
        return {
          type: node.type,
          pong: node.pong,
          answered: node.answered,
          name: displayName,
          segment_type: node.segment_type,
        };
      });
      return finalSimplified;
      // return finalSimplified.filter((item) => item.segment_type !== "pong");
    },
    /** Icon mapping */
    getFlowIcon(type) {
      switch (true) {
        case type === "world":
          return "globe";
        case type === "queue":
          return "headset";
        case type === "extension":
          return "user";
        case type === 148 || type === "ivr":
          return "phone";
        default:
          return "circle";
      }
    },
    getFlowIconClass(type, answered, pong) {
      switch (true) {
        case type === "world":
          return "text-gray-400";
        case type === "queue":
          return "text-amber-500";
        case type === "extension":
          if (answered) {
            return "text-green-500";
          } else {
            return "text-red-500";
          }
        // return "text-teal-600";
        case type === 148 || type === "ivr":
          return "text-indigo-500";
        default:
          return "text-gray-500";
      }
    },
  },
  computed: {
    ...mapGetters(["pbxMap"]),
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500;
}
</style>
